/*
baseProvider.tsx
Copyright © 2026 Xerox Corporation. All Rights Reserved.

Copyright protection claimed includes all forms and matters of copyrightable material
and information now allowed by statutory or judicial law or hereinafter granted,
including without limitation, material generated from the software programs which
are displayed on the screen such as icons, screen display looks, etc.
*/

import {
  getAppGallerySettings,
  AppGallerySettings
} from '../modules/appGallery.ts'
import { sessionVar } from '../constants/storage.const.ts'
import { setSessionVar } from '../modules/browserStorage.ts'
import {
  useState,
  createContext,
  useEffect,
  ReactNode,
  useContext,
  Dispatch,
  SetStateAction
} from 'react'
import { EcommerceSummary } from '../types/eCommerceSummary.ts'
import { ErrorType } from '../modules/errors.ts'
import {
  trackEcommerceConsumption,
  ecommerceUnit,
  fetchEcommerceSummary,
  getEcommerceRemainingCredits
} from '../modules/ecommerce.ts'
import { errorTypes } from '../constants/errorTypes.ts'
import { useInfoModalContext } from './useInfoModalContext.tsx'
import ErrorBody from '../routes/Scan/errorBody.tsx'
import { exitApp } from '../modules/util.ts'
import { useTranslation } from 'react-i18next'
import { localVar } from '../constants/storage.const.ts'
import usePersistentState from '../hooks/useBrowserStorage.tsx'
import { DeviceInfoProps } from '../types/deviceInfoProps.ts'
import { AxiosError } from 'axios'

interface BaseContextProps {
  activity?: string
  setActivity: (activity: string | undefined) => void
  deviceId?: string
  appId?: string
  deviceInformation?: DeviceInfoProps
  setDeviceInformation: (info: DeviceInfoProps) => void
  ecommerceSummary: EcommerceSummary | undefined
  setEcommerceSummary: Dispatch<SetStateAction<EcommerceSummary | undefined>>
  isEnterpriseEcommerce: boolean
  setError: (error: ErrorType | undefined, fullScreen?) => void
  ecommerceBalance: number | undefined
  trackCredits: (usage: number) => Promise<void> | undefined
  appLoading: boolean
  setAppLoading: Dispatch<SetStateAction<boolean>>
  setEcommerceBalance: Dispatch<SetStateAction<number | undefined>>
  getEcommerceSummary: () => void
  ecommerceEnabled: boolean
  eulaLastAccepted: string
  setEulaLastAccepted: (timestamp: string) => void
  firstRunAfterEulaAcceptance: boolean
  setFirstRunAfterEulaAcceptance: Dispatch<SetStateAction<boolean>>
  isAlta: boolean
  getRemainingCredits: () => Promise<number>
}

export const BaseContext = createContext<BaseContextProps | undefined>(
  undefined
)

interface BaseProviderProps {
  children: ReactNode
}

const BaseProvider: React.FC<BaseProviderProps> = ({ children }) => {
  const { t } = useTranslation()
  const [activity, setActivity] = useState<string | undefined>()
  const [deviceInformation, setDeviceInformation] = useState<
    DeviceInfoProps | undefined
  >()
  const [appGallerySettings, setAppGallerySettings] =
    useState<AppGallerySettings>()
  const [appLoading, setAppLoading] = useState(true)
  const [eulaLastAccepted, setEulaLastAccepted] = usePersistentState<string>(
    localVar.eulaAccepted,
    ''
  )
  const [firstRunAfterEulaAcceptance, setFirstRunAfterEulaAcceptance] =
    useState<boolean>(false)
  const [ecommerceBalance, setEcommerceBalance] = useState<number>()
  const [isEnterpriseEcommerce, setIsEnterpriseEcommerce] =
    useState<boolean>(false)
  const [ecommerceSummary, setEcommerceSummary] = useState<EcommerceSummary>()
  const [isAlta, setIsAlta] = useState<boolean>(false)
  const { deviceid, apikey, appid } = appGallerySettings || {}
  const infoModalContext = useInfoModalContext()
  const ecommerceEnabled = import.meta.env.VITE_APP_ECOMMERCE == 'true'
  const getRemainingCredits = async () => {
     if (
      deviceInformation?.serial &&
      appid &&
      ecommerceSummary &&
      ecommerceSummary.remainingDays > 0 &&
      ecommerceSummary.usage
    ) {
      try {
        const balance = await getEcommerceRemainingCredits(
          deviceInformation.serial,
          appid,
          e => {
            if (e.response?.status === 504) {
              setError(errorTypes.ecommerceTimeout)
            } else if (e?.response?.status === 500) {
              setError(errorTypes.ecommerceError)
            } else {
              setError(errorTypes.apiError)
            }
          }
        )
        setEcommerceBalance(balance)
        return balance
      } catch (err) {
        setError(errorTypes.ecommerceError)
        throw err
      }
    } else {
      setError(errorTypes.ecommerceError)
      throw new Error('Missing or invalid ecommerce/device information')
    }
  }

  useEffect(() => {
    const sessionVariables = [{ key: sessionVar.apiKey, value: apikey }]
    sessionVariables.forEach(s => {
      if (s.value) {
        setSessionVar(s.key, s.value)
      }
    })
    setAppGallerySettings(getAppGallerySettings())
  }, [apikey])

  useEffect(() => {
    setIsEnterpriseEcommerce(ecommerceBalance === -1)
  }, [ecommerceBalance])

  useEffect(() => {
    if (ecommerceSummary && appid && deviceInformation?.serial) {
      getRemainingCredits()
    }
  }, [ecommerceSummary, deviceInformation?.serial, appid])

  useEffect(() => {
    if (deviceInformation) {
      setIsAlta(deviceInformation.model.toLowerCase().includes('alta'))
    }
  }, [deviceInformation])

  const getEcommerceSummary = () => {
    if (deviceInformation?.serial && appid) {
      fetchEcommerceSummary(deviceInformation?.serial, appid)
        .then(c => {
          setEcommerceSummary(c)
        })
        .catch((e: AxiosError) => {
          if (e.response?.status == 504) {
            setError(errorTypes.ecommerceTimeout)
          } else if (e?.response?.status === 500) {
            setError(errorTypes.ecommerceError)
          } else {
            setError(errorTypes.apiError)
          }
        })
        .catch(() => setError(errorTypes.ecommerceError))
    } else {
      setError(errorTypes.appUnavailable)
    }
  }

  const trackCredits = (usage: number) => {
    if (deviceInformation?.serial && appid) {
      return trackEcommerceConsumption(
        deviceInformation.serial,
        appid,
        usage,
        ecommerceUnit
      )
        .then(() => getEcommerceSummary())
        .catch((e: AxiosError) => {
          if (e.response?.status == 504) {
            setError(errorTypes.ecommerceTimeout)
          } else if (e?.response?.status === 500) {
            setError(errorTypes.ecommerceError)
          } else {
            setError(errorTypes.apiError)
          }
        })
        .catch(() => setError(errorTypes.ecommerceError))
    }
  }
  const setError = (error: ErrorType | undefined, fullScreen?: boolean) => {
    if (error) {
      const staticLoading = document.getElementById('apploading-root')
      if (staticLoading) {
        staticLoading.remove()
      }
      infoModalContext.setModalProperties({
        titleKey: error.titleKey,
        body: ErrorBody(error, t),
        fullScreen: fullScreen ?? true,
        infoType: 'error',
        show: true,
        onClose: () => (error.exitApp ? exitApp() : {}),
        autoclose: error.autoclose
      })
    } else {
      infoModalContext.setModalProperties(undefined)
    }
  }

  const contextElements: BaseContextProps = {
    activity,
    setActivity,
    deviceId: deviceid,
    appId: appid,
    deviceInformation,
    setDeviceInformation,
    ecommerceSummary,
    setEcommerceSummary,
    setError,
    isEnterpriseEcommerce,
    ecommerceBalance,
    trackCredits,
    appLoading,
    setAppLoading,
    setEcommerceBalance,
    getEcommerceSummary,
    ecommerceEnabled,
    eulaLastAccepted,
    setEulaLastAccepted,
    firstRunAfterEulaAcceptance,
    setFirstRunAfterEulaAcceptance,
    isAlta,
    getRemainingCredits
  }

  return (
    <BaseContext.Provider value={contextElements}>
      {children}
    </BaseContext.Provider>
  )
}
export const useBaseContext = () => {
  const context = useContext(BaseContext)
  if (!context) {
    throw new Error('useBaseContext must be used within a BaseProvider')
  }
  return context
}
export default BaseProvider
